import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_TRACKING_MODE,
  TRACKING_MODE_STORAGE_KEY,
} from 'core/config/maps';
import type { TrackingDisplayMode } from 'types/tracking.types';

function readStoredMode(): TrackingDisplayMode | null {
  try {
    const v = localStorage.getItem(TRACKING_MODE_STORAGE_KEY);
    if (v === 'map' || v === 'simple') return v;
  } catch {
    // ignore
  }
  return null;
}

export function useTrackingMode() {
  const [userMode, setUserMode] = useState<TrackingDisplayMode | null>(
    readStoredMode,
  );
  const [mapFailed, setMapFailed] = useState(false);

  const effectiveMode: TrackingDisplayMode = (() => {
    if (userMode) return userMode;
    if (DEFAULT_TRACKING_MODE === 'map') return mapFailed ? 'simple' : 'map';
    if (DEFAULT_TRACKING_MODE === 'simple') return 'simple';
    return mapFailed ? 'simple' : 'map';
  })();

  const setMode = useCallback((mode: TrackingDisplayMode) => {
    setUserMode(mode);
    setMapFailed(false);
    try {
      localStorage.setItem(TRACKING_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, []);

  const onMapError = useCallback(() => {
    setMapFailed(true);
  }, []);

  useEffect(() => {
    if (mapFailed && !userMode) {
      // auto fallback — no persist
    }
  }, [mapFailed, userMode]);

  return { mode: effectiveMode, setMode, onMapError, mapFailed };
}
